<template>
    <v-row no-gutters class="px-6 pt-6">
        <Dialog
            v-if="roleDialog.show"
            :value="roleDialog.show"
            :header-text="$t('Contact role')"
            header_classes="black white--text"
            persistent
            :width="450"
            @close_modal="closeRoleDialog"
        >
            <v-container v-if="roleDialog.data">
                <v-row no-gutters>
                    <v-col cols="12">
                        <v-text-field
                            v-model="roleDialog.data.name"
                            :error-messages="roleDialog.data.name_error"
                            :label="$t('Role')"
                            outlined
                        />
                    </v-col>
                </v-row>
            </v-container>

            <template v-if="roleDialog.data" slot="footer">
                <v-btn v-if="roleDialog.data.id" text color="error" @click="deleteRoleClick">
                    {{ $t("Delete") }}
                </v-btn>
                <v-spacer />
                <v-btn text @click="closeRoleDialog">
                    {{ $t("Close") }}
                </v-btn>
                <v-btn class="primary inverted--text" @click="saveRoleClick">
                    {{ $t("OK") }}
                </v-btn>
            </template>
        </Dialog>

        <v-col v-if="dataLoaded" cols="12">
            <v-row no-gutters>
                <v-col>
                    <AppStoreListHeader
                        :item-label="$t('Contact roles')"
                        :add-label="$t('Add')"
                        :show-button="appStoreDialogFlags.canManageAccount()"
                        @add-item="editRoleClick(null)"
                    />
                    <data-table
                        :items="state.contact_roles"
                        :headers="headers.contact_roles"
                        :row-clickable="appStoreDialogFlags.canManageAccount()"
                        @click:row="appStoreDialogFlags.canManageAccount() && editRoleClick($event)"
                    />
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
import { reactive, computed, ref, watch } from "@vue/composition-api";
import { $t } from "@/i18n";
import AccountModule from "../../../store/modules/account";
import AppModule from "../../../store/modules/app";
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import Dialog from "../../../components/Dialog";
import Vue from "vue";
import { removeFromList, syncItemToList } from "../../../plugins/formatting";
import RoleModule from "../../../store/modules/role";
import AppStoreListHeader from "../../../components/AppStore/AppStoreListHeader";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import {deepClone} from "@/helpers/helpers";

export default {
    name: "ClientsDialogSettingsTab",
    components: {
        AppStoreListHeader,
        Dialog
    },
    props: {
        isSelected: {
            type: Boolean,
            default: false
        }
    },
    setup(props) {
        const { fetchClassifiers, roles, validateFormData } = AppModule();
        const { appStoreDialogGetters, appStoreDialogActions, appStoreDialogFlags } = appStoreDialog()
        const { deleteRole } = RoleModule();

        const loading = ref(false);
        const dataLoaded = ref(false);
        const roleDialog = ref({
            show: false,
            data: null
        });
        const state = reactive({
            contact_roles: []
        });
        const headers = ref({
            contact_roles: [
                { value: "name", text: $t("Name"), sortable: false },
                { value: "edit" }
            ]
        });
        const counter = ref(1);

        const selectedAccount = computed(() => {
            return appStoreDialogGetters.getSelectedCompany()
        });

        function saveSettings() {
            loading.value = true;
            const data = {
                id: selectedAccount.value.id,
                roles: state.contact_roles
            };
            putAccountSetup(data)
                .then(() => {
                    let reqData = {
                        types: ["roles"],
                        account_id: selectedAccount.value.id,
                        force: { default: true, types: ["roles"] }
                    };
                    fetchClassifiers(reqData)
                    loading.value = false;
                    appStoreDialogActions.updateCurrentTab(0);
                })
                .catch(ex => {
                    loading.value = false;
                    errorDialog.showError(ex);
                });
        }

        const { putAccountSetup } = AccountModule();

        const fetchData = function(){
            let reqData = { types: ["roles"], account_id: selectedAccount.value.id };
            fetchClassifiers(reqData)
                .then(() => {
                    state.contact_roles = deepClone(roles.value[selectedAccount.value.id]);
                    dataLoaded.value = true;
                })
                .catch(ex => errorDialog.showError(ex));
        }

        const closeRoleDialog = () => {
            roleDialog.value.show = false;
            roleDialog.value.data = null;
        }
        const editRoleClick = (item) => {
            roleDialog.value.data = Vue.prepare_form_data(
                item == null ? { id: null, name: "" } : Vue.clone_object(item),
                { name: "text" }
            );
            roleDialog.value.show = true;
        }
        const deleteRoleClick = () => {
            if (typeof roleDialog.value.data.id === "string" && roleDialog.value.data.id.startsWith("tmp")) {
                removeFromList(state.contact_roles, roleDialog.value.data.id);
                closeRoleDialog();
            }

            deleteRole(selectedAccount.value.id, roleDialog.value.data.id, { validate_only: 1 })
                .then(() => {
                    removeFromList(state.contact_roles, roleDialog.value.data.id);
                    closeRoleDialog();
                })
                .catch(ex => errorDialog.showError(ex));
        };
        const saveRoleClick = () => {
            if (!validateFormData(roleDialog.value.data, {
                name: { type: "text", required: true }
            })) return;

            if (roleDialog.value.data.id == null) {
                counter.value++;
                roleDialog.value.data.id = "tmp{0}".format(counter.value);
            }
            syncItemToList(state.contact_roles, roleDialog.value.data);
            closeRoleDialog();
        };

        fetchData()

        watch (() => props.isSelected, () => {
            if (props.isSelected) {
                appStoreDialogActions.setFooterActions([
                    {
                        text: $t("Save"),
                        class: "primary",
                        loading: loading,
                        click: saveSettings
                    }
                ]);
            }
        }, { immediate: true })

        return {
            loading,
            state,
            appStoreDialogFlags,
            dataLoaded,
            headers,
            roleDialog,
            saveSettings,
            editRoleClick,
            closeRoleDialog,
            saveRoleClick,
            deleteRoleClick
        };
    }
}
</script>
